import type { SuccessPhpResponse } from '@/types';
import type { GeneralType } from './general.types';
import { createSlice } from '@reduxjs/toolkit';
import { getClientSecretTargetUrl, whichUrlShouldBePassed } from '@/utils/utils';

const initialState = {
  page: 'slider',
  extendedSliderHeight: 0,
  title: '',
  isContactFormSubmitted: false,
  windowLocationSearch: '',
  session: null,
  clientSecretUrl: getClientSecretTargetUrl(whichUrlShouldBePassed(location.href)).replace('{clientSecretId}', '0'),
  secret: '',
  leadId: '',
  testVariation: '',
  forcedPriorityFour: false,
  leadPlatform: '',
  isLongReadShown: false,
  showNewTanksPage: false,
  userFormHeight: 0,
  withArticle: false,
  logo: { desktop: '', mobile: '', rightmart: '' },
  prioVersion: {
    v2: null,
    v3: null,
    generic: null,
  },
  longReadLead: 0,
  brazeProspect: false,
  loseLead: false,
  qid: {},
  formUpload: {
    completed: false,
    loading: false,
    successful: false,
  },
  dynamicLandingPageState: 'loading',
  isNewClient: false,
  debtAnalysis: {
    rate: '',
    months: '',
  },
} satisfies GeneralType as GeneralType;

const GeneralSlice = createSlice({
  name: 'general',
  initialState,
  reducers: (create) => ({
    setPage: create.reducer<GeneralType['page']>((state, action) => {
      state.page = action.payload;
    }),
    setSession: create.reducer<GeneralType['session']>((state, action) => {
      state.session = action.payload;
    }),
    setSecret: create.reducer<GeneralType['secret']>((state, action) => {
      state.secret = action.payload;
    }),
    setLeadId: create.reducer<GeneralType['leadId']>((state, action) => {
      state.leadId = action.payload;
    }),
    setClientSecretUrl: create.reducer<GeneralType['clientSecretUrl']>((state, action) => {
      state.clientSecretUrl = action.payload;
    }),
    setWindowLocationSearch: create.reducer<GeneralType['windowLocationSearch']>((state, action) => {
      state.windowLocationSearch = action.payload;
    }),
    setIsContactFormSubmitted: create.reducer<GeneralType['isContactFormSubmitted']>((state, action) => {
      state.isContactFormSubmitted = action.payload;
    }),
    setExtendedSliderHeight: create.reducer<GeneralType['extendedSliderHeight']>((state, action) => {
      state.extendedSliderHeight = action.payload;
    }),
    setIsLongReadShown: create.reducer<GeneralType['isLongReadShown']>((state, action) => {
      state.isLongReadShown = action.payload;
    }),
    setShowNewTanksPage: create.reducer<GeneralType['showNewTanksPage']>((state, action) => {
      state.showNewTanksPage = action.payload;
    }),
    setUserFormHeight: create.reducer<GeneralType['userFormHeight']>((state, action) => {
      state.userFormHeight = action.payload;
    }),
    setWithArticle: create.reducer<GeneralType['withArticle']>((state, action) => {
      state.withArticle = action.payload;
    }),
    setLogo: create.reducer<GeneralType['logo']>((state, action) => {
      state.logo = action.payload;
    }),
    setLongReadLead: create.reducer<GeneralType['longReadLead']>((state, action) => {
      state.longReadLead = action.payload;
    }),
    setQid: create.reducer<GeneralType['qid']>((state, action) => {
      state.qid = action.payload;
    }),
    setFormUploadCompleted: create.reducer<GeneralType['formUpload']['completed']>((state, action) => {
      state.formUpload.completed = action.payload;
    }),
    setFormUploadLoading: create.reducer<GeneralType['formUpload']['loading']>((state, action) => {
      state.formUpload.loading = action.payload;
    }),
    setFormUploadSuccessful: create.reducer<GeneralType['formUpload']['successful']>((state, action) => {
      state.formUpload.successful = action.payload;
    }),
    setDynamicLandingPageState: create.reducer<GeneralType['dynamicLandingPageState']>((state, action) => {
      state.dynamicLandingPageState = action.payload;
    }),
    setIsNewClient: create.reducer<GeneralType['isNewClient']>((state, action) => {
      state.isNewClient = action.payload;
    }),
    setDebtAnalysis: create.reducer<GeneralType['debtAnalysis']>((state, action) => {
      state.debtAnalysis = action.payload;
    }),
    updateAfterSuccessPhp: create.reducer<{ data: SuccessPhpResponse; useDynamicLandingPage: boolean }>(
      (state, action) => {
        const { data, useDynamicLandingPage } = action.payload;
        state.loseLead = data.lose_lead || initialState.loseLead;
        state.dynamicLandingPageState = 'done';
        // TODO: Do we have here any kind of priority?
        state.leadId = data.leadId || data.externalId || state.leadId || initialState.leadId;
        state.testVariation = data.testVariation || initialState.testVariation;
        state.forcedPriorityFour = data.forcedPriorityFour || initialState.forcedPriorityFour;
        state.leadPlatform = data.platform || initialState.leadPlatform;
        state.session = data.session || initialState.session;
        if (useDynamicLandingPage) {
          state.prioVersion = {
            v2: data.prioV2 || {
              prio: 0,
              score: 0,
              success: false,
            },
            v3: data.RandomizerV3
              ? {
                  prio: data.RandomizerV3,
                  score: data.prioScoreV3 || 0,
                  success: true,
                }
              : {
                  prio: 0,
                  score: 0,
                  success: false,
                },
            generic: data.prioScore
              ? {
                  prio: data.prioScore,
                  score: data.prioConversionProbability || 0,
                  success: false,
                }
              : {
                  prio: 0,
                  score: 0,
                  success: false,
                },
          };
          state.longReadLead = data.longReadLead || initialState.longReadLead;
          state.brazeProspect = data.brazeProspect || initialState.brazeProspect;
        }
      },
    ),
    toInitialState: create.reducer(() => initialState),
  }),
  selectors: {
    general: (general) => general,
    page: (general) => general.page,
    extendedSliderHeight: (general) => general.extendedSliderHeight,
    title: (general) => general.title,
    isContactFormSubmitted: (general) => general.isContactFormSubmitted,
    windowLocationSearch: (general) => general.windowLocationSearch,
    session: (general) => general.session,
    clientSecretUrl: (general) => general.clientSecretUrl,
    secret: (general) => general.secret,
    leadId: (general) => general.leadId,
    testVariation: (general) => general.testVariation,
    forcedPriorityFour: (general) => general.forcedPriorityFour,
    leadPlatform: (general) => general.leadPlatform,
    isLongReadShown: (general) => general.isLongReadShown,
    showNewTanksPage: (general) => general.showNewTanksPage,
    userFormHeight: (general) => general.userFormHeight,
    withArticle: (general) => general.withArticle,
    logo: (general) => general.logo,
    prioV2: (general) => general.prioVersion.v2,
    prioV3: (general) => general.prioVersion.v3,
    prioGeneric: (general) => general.prioVersion.generic,
    longReadLead: (general) => general.longReadLead,
    brazeProspect: (general) => general.brazeProspect,
    loseLead: (general) => general.loseLead,
    qid: (general) => general.qid,
    formUploadCompleted: (general) => general.formUpload.completed,
    formUploadLoading: (general) => general.formUpload.loading,
    formUploadSuccessful: (general) => general.formUpload.successful,
    dynamicLandingPageState: (general) => general.dynamicLandingPageState,
    isNewClient: (general) => general.isNewClient,
    debtAnalysis: (general) => general.debtAnalysis,
  },
});

export default GeneralSlice;
